<script setup lang="ts">
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import type { Question } from '../calendarQuestion'
import CalendarQuestionSample from './CalendarQuestionSample.vue'
import IconChevron from '@/components/icons/IconChevron.vue'
import IconTrash from '@/components/icons/IconTrash.vue'

const { question, isSelected, formError } = defineProps<{
  question: Question
  isSelected: boolean
  formError: string | undefined
}>()

const emit = defineEmits<{
  (event: 'select' | 'up' | 'down' | 'remove'): void
}>()
</script>

<template>
  <div
    :class="[$style.question, { [$style['question--selected']]: isSelected }]"
    @click.prevent="emit('select')"
  >
    <div :class="$style.upDownControl">
      <ButtonIconOnly
        :class="$style.upDownControl__button"
        @click.prevent.stop="emit('up')"
      >
        <template #icon><IconChevron direction="up" /></template>
      </ButtonIconOnly>
      <ButtonIconOnly
        :class="$style.upDownControl__button"
        @click.prevent.stop="emit('down')"
      >
        <template #icon> <IconChevron direction="down" /> </template>
      </ButtonIconOnly>
    </div>
    <CalendarQuestionSample
      :class="[$style.sample, { [$style['sample--selected']]: isSelected }]"
      :question="question"
      :formError="formError"
    />
    <div :class="$style.removeControl">
      <ButtonIconOnly
        :class="$style.removeControl__button"
        @click.prevent="emit('remove')"
      >
        <template #icon> <IconTrash /> </template>
      </ButtonIconOnly>
    </div>
  </div>
</template>

<style lang="scss" module>
.question {
  transition: $transition-base;
  display: flex;

  &:hover {
    background-color: $color-surface-accent-primary-light;
  }

  &--selected {
    background-color: $color-focus-ambiguous;

    &:hover {
      background-color: $color-focus-ambiguous;
    }
  }

  cursor: pointer;

  & * {
    cursor: pointer;
  }
}

.upDownControl {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  gap: 8px;
  padding: 8px;

  &__button {
    border-radius: 100%;
    padding: 4px;

    &:hover {
      background-color: $color-surface-accent-primary;
      color: $color-text-and-object-high-emphasis-inverse;
      transition: $transition-interaction;
    }
  }
}

.removeControl {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;

  &__button {
    border-radius: 100%;
    padding: 8px;
    color: $color-text-and-object-accent-red;

    svg {
      width: 20px;
      height: 20px;
    }

    &:hover {
      background-color: $color-surface-accent-red;
      color: $color-text-and-object-high-emphasis-inverse;
      transition: $transition-interaction;
    }
  }
}

.sample {
  flex: 1;
  padding: 16px 0;
}
</style>
